import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {formatDate} from '../utils/dateTime';
import createAxiosInstance from '../support/AxiosSupport';
import {hasPermission, useWardrobe} from '../context/WardrobeContext';
import Spinner from '../components/Spinner';
import AuthImage from '../components/AuthImage';
import {
    CoinsIcon,
    DisposalIcon,
    HeartIcon,
    RecycleIcon,
    SearchIcon,
    ShirtIcon,
    TrashIcon,
    UsersIcon,
} from '../components/icons';
import {
    DISPOSAL_METHOD_LABELS,
    DisposalIntent,
    ItemLifecycleStatus,
    LIFECYCLE_LABELS,
    RETIRED_STATUSES,
    retirementLabel,
} from '../support/itemLifecycle';
import {disposalSourceLabel} from '../support/disposalSources';
import {
    DisposalArchiveFilter,
    DisposalArchiveSummary,
    archiveFilterParams,
    correctDisposalEvent,
    fetchArchiveSummary,
} from '../service/DisposalApi';
import {pluralItems} from '../utils/plural';
import logger from '../utils/logger';
import PageSizeSelect from '../components/PageSizeSelect';
import {usePageSize} from '../hooks/usePageSize';

/**
 * Стадия «Выбыли» раздела «Выбытие» — история расставаний.
 *
 * <p>Раньше раздел назывался «Утилизированные», хотя содержал и отданные, и пожертвованные
 * вещи: одно из выбытий подменяло собой все. Теперь у каждой строки виден её собственный
 * способ и дата — это и есть ответ на вопрос «что с ней в итоге стало».</p>
 *
 * <p>Полсотни вещей на трёх страницах с кнопками «Назад/Вперёд» — это список, а не история
 * (D13). Историю отличает то, что в ней можно найти вещь, отобрать срез и подвести итог:
 * сколько уходит через переработку, а сколько на свалку; сам ли человек доводит решения до
 * конца. Поэтому здесь поиск, фильтры и два разреза — по каналам и по источникам решения.</p>
 */

interface DisposedItem {
    id: string;
    name: string;
    categoryName: string | null;
    brandName: string | null;
    sizeName: string | null;
    disposalDate: string | null;
    firstPhotoUrl: string | null;
    lifecycleStatus?: ItemLifecycleStatus | null;
}

interface PageResponse {
    content: DisposedItem[];
    totalElements: number;
    totalPages: number;
}

/** Пауза перед запросом по поисковой строке: набор текста не должен слать запрос на букву. */
const SEARCH_DEBOUNCE_MS = 350;

const EMPTY_FILTER: DisposalArchiveFilter = {q: '', method: [], categoryId: null, from: null, to: null};

/** Разные значки способов выбытия вместо одного общего: способ читается с одного взгляда. */
const MethodIcon: React.FC<{status?: ItemLifecycleStatus | null; className?: string}> = ({status, className = 'w-4 h-4'}) => {
    switch (status) {
        case 'DONATED': return <HeartIcon className={className}/>;
        case 'GIVEN_AWAY': return <UsersIcon className={className}/>;
        case 'SOLD': return <CoinsIcon className={className}/>;
        case 'RECYCLED': return <RecycleIcon className={className}/>;
        default: return <TrashIcon className={className}/>;
    }
};

const methodLabel = (code: string): string =>
    LIFECYCLE_LABELS[code as ItemLifecycleStatus] ?? code;

/**
 * Исправление ошибочно записанного выбытия (аудит 17.09, D05).
 *
 * <p>В архиве не было ничего, кроме «Удалить (ошибочная запись)», хотя это два разных
 * исправления: «я ошибся, отметив выбытие» — вещь надо вернуть в гардероб, а «такой вещи вообще
 * не должно быть» — запись надо удалить. Удаление осталось отдельным редким действием.</p>
 */
function CorrectionPanel({itemId, name, onDone}: {
    itemId: string;
    name: string;
    onDone: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [method, setMethod] = useState<DisposalIntent | ''>('');
    const [date, setDate] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (action: 'RETURN' | 'AMEND') => {
        setBusy(true);
        setError(null);
        try {
            await correctDisposalEvent(itemId, {
                action,
                method: action === 'AMEND' && method ? method : null,
                disposalDate: action === 'AMEND' && date ? date : null,
            });
            setOpen(false);
            onDone();
        } catch (e) {
            logger.debug('DisposedItemsPage: correction failed', {error: (e as Error)?.message});
            setError('Не удалось исправить событие. Проверьте способ и дату и попробуйте ещё раз.');
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)}
                    data-testid="disposed-correct"
                    className="inline-flex items-center min-h-[44px] text-xs text-brand-600
                               dark:text-brand-400 hover:underline">
                Исправить событие выбытия
            </button>
        );
    }
    return (
        <div className="mt-2 rounded-xl border border-gray-200 dark:border-ink-700 p-3 space-y-2"
             data-testid="disposed-correction-panel">
            <p className="text-xs text-gray-600 dark:text-gray-300">
                «{name}»: вещь не выбывала — верните её в гардероб; выбыла иначе — поправьте
                способ или дату. История сохранится.
            </p>
            <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-gray-600 dark:text-gray-300">
                    <span className="block mb-1">Способ</span>
                    <select value={method} data-testid="disposed-correct-method"
                            onChange={e => setMethod(e.target.value as DisposalIntent | '')}
                            className="input min-h-[44px] rounded-xl text-sm">
                        <option value="">Не менять</option>
                        {(Object.keys(DISPOSAL_METHOD_LABELS) as DisposalIntent[]).map(m => (
                            <option key={m} value={m}>{DISPOSAL_METHOD_LABELS[m]}</option>
                        ))}
                    </select>
                </label>
                <label className="text-xs text-gray-600 dark:text-gray-300">
                    <span className="block mb-1">Дата</span>
                    <input type="date" value={date} data-testid="disposed-correct-date"
                           onChange={e => setDate(e.target.value)}
                           className="input min-h-[44px] rounded-xl text-sm"/>
                </label>
                <button type="button" disabled={busy} onClick={() => void submit('AMEND')}
                        data-testid="disposed-correct-amend"
                        className="btn-secondary min-h-[44px] px-3 rounded-xl text-xs">
                    Сохранить способ и дату
                </button>
                <button type="button" disabled={busy} onClick={() => void submit('RETURN')}
                        data-testid="disposed-correct-return"
                        className="btn-secondary min-h-[44px] px-3 rounded-xl text-xs">
                    Вернуть в гардероб
                </button>
                <button type="button" disabled={busy} onClick={() => setOpen(false)}
                        className="min-h-[44px] px-3 rounded-xl text-xs text-gray-500 hover:underline">
                    Отмена
                </button>
            </div>
            {error && (
                <p className="text-xs text-rose-600 dark:text-rose-400" role="alert"
                   data-testid="disposed-correct-error">{error}</p>
            )}
        </div>
    );
}

const DisposedItemsPage: React.FC = () => {
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();
    const canView = isOwnWardrobe || hasPermission(permissions, 'VIEW_THROWN_AWAY');
    // Исправление события — то же право, что и само выбытие: кто отметил факт, тот и правит.
    const canCorrect = isOwnWardrobe || hasPermission(permissions, 'THROW_AWAY_IMMEDIATE');
    const permissionsLoaded = isOwnWardrobe || permissions !== null;

    const [items, setItems] = useState<DisposedItem[]>([]);
    const [summary, setSummary] = useState<DisposalArchiveSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    // Сколько записей архива показывать за раз — выбор пользователя, он сохраняется (CTHS-7).
    // Смена размера возвращает к первой странице: номер прежней после пересчёта означает
    // другое место истории.
    const [pageSize, setPageSize] = usePageSize('disposal-archive', {onChange: () => setPage(0)});

    // Строка поиска живёт отдельно от применённого фильтра: между ними — пауза набора.
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<DisposalArchiveFilter>(EMPTY_FILTER);

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilter(f => (f.q === search ? f : {...f, q: search}));
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Срез изменился — страница снова первая: иначе третья страница пустого результата.
    useEffect(() => {
        setPage(0);
    }, [filter]);

    const filtered = useMemo(
        () => Boolean(filter.q?.trim() || filter.method?.length || filter.categoryId || filter.from || filter.to),
        [filter],
    );

    const load = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params: Record<string, unknown> = {page, size: pageSize, ...archiveFilterParams(filter)};
            if (selectedOwnerId) params.ownerId = selectedOwnerId;
            const [res, totals] = await Promise.all([
                api.get<PageResponse>('/clothing/disposed', {params}),
                // Итоги — подсказка, а не содержимое: без них список остаётся рабочим.
                fetchArchiveSummary(selectedOwnerId, filter).catch(() => null),
            ]);
            setItems(res.data.content);
            setTotalPages(res.data.totalPages);
            setTotal(res.data.totalElements);
            setSummary(totals);
        } catch (e) {
            logger.debug('DisposedItemsPage: load failed', {error: (e as Error)?.message});
            setError(true);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [canView, page, pageSize, selectedOwnerId, filter]);

    useEffect(() => {
        void load();
    }, [load]);

    const reset = () => {
        setSearch('');
        setFilter(EMPTY_FILTER);
    };

    const toggleMethod = (code: string) => {
        setFilter(f => {
            const current = f.method ?? [];
            return {
                ...f,
                method: current.includes(code) ? current.filter(m => m !== code) : [...current, code],
            };
        });
    };

    if (!permissionsLoaded) {
        return <div className="flex justify-center py-12" data-testid="disposed-loading"><Spinner/></div>;
    }

    if (!canView) {
        return (
            <div className="card p-12 text-center" data-testid="disposed-no-access">
                <DisposalIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"/>
                <p className="text-gray-600 dark:text-gray-400 text-lg">Нет доступа к выбывшим вещам</p>
                <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                    Нужно право «Просмотр утилизированных» в этом гардеробе
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Выбывшие вещи{total > 0 ? `: ${pluralItems(total)}` : ''}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="disposed-subtitle">
                    Вещи, которые покинули гардероб. Они не участвуют в подборах, но история носки
                    и оценок сохраняется — видно, чем вы пользовались и как с этим расстались.
                </p>
            </div>

            <ArchiveFilters
                search={search}
                filter={filter}
                summary={summary}
                filtered={filtered}
                onSearch={setSearch}
                onToggleMethod={toggleMethod}
                onChange={patch => setFilter(f => ({...f, ...patch}))}
                onReset={reset}
            />

            {summary && <ArchiveTotals summary={summary}/>}

            {loading && <div className="flex justify-center py-12" data-testid="disposed-loading"><Spinner/></div>}

            {!loading && error && (
                <div className="card p-12 text-center" data-testid="disposed-error">
                    <DisposalIcon className="w-16 h-16 mx-auto text-rose-400 dark:text-rose-500 mb-4"/>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Не удалось загрузить выбывшие вещи</p>
                    <button onClick={() => void load()} className="btn-secondary mt-4 min-h-[44px] px-4 rounded-xl">
                        Повторить
                    </button>
                </div>
            )}

            {!loading && !error && items.length === 0 && (
                filtered ? (
                    /* Пустой срез и пустой архив — разные новости: не дать спутать «не нашлось»
                       с «ещё ничего не выбыло». */
                    <div className="card p-12 text-center" data-testid="disposed-nothing-found">
                        <SearchIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"/>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">Ничего не найдено</p>
                        <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                            В архиве есть вещи, но под этот срез не попала ни одна.
                        </p>
                        <button type="button" onClick={reset} data-testid="disposed-reset-empty"
                                className="btn-secondary mt-4 min-h-[44px] px-4 rounded-xl">
                            Сбросить фильтры
                        </button>
                    </div>
                ) : (
                    <div className="card p-12 text-center" data-testid="disposed-empty">
                        <DisposalIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"/>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">Ещё ничего не выбыло</p>
                        <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                            Вещи попадают сюда, когда вы отмечаете, что отдали, пожертвовали, сдали на
                            переработку или выбросили их.
                        </p>
                    </div>
                )
            )}

            {!loading && !error && items.length > 0 && (
                <ul className="card divide-y divide-gray-100 dark:divide-white/5" data-testid="disposed-list">
                    {items.map(item => (
                        <li key={item.id} className="p-3 flex gap-3 items-start" data-testid="disposed-row">
                            <Link to={`/clothing/${item.id}`} className="shrink-0" tabIndex={-1} aria-hidden="true">
                                {item.firstPhotoUrl ? (
                                    <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                                               className="w-16 h-16 rounded-lg object-cover"/>
                                ) : (
                                    <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-ink-700 flex
                                                    items-center justify-center text-gray-300 dark:text-gray-600">
                                        <ShirtIcon className="w-7 h-7"/>
                                    </div>
                                )}
                            </Link>
                            <div className="min-w-0 flex-1">
                                <Link to={`/clothing/${item.id}`}
                                      className="font-medium text-gray-900 dark:text-gray-100 hover:text-brand-600
                                                 dark:hover:text-brand-400 hover:underline break-words">
                                    {item.name}
                                </Link>
                                <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                    {[item.brandName, item.sizeName && `размер ${item.sizeName}`, item.categoryName]
                                        .filter(Boolean).join(' · ') || 'Без описания'}
                                </p>
                                <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300"
                                   data-testid="disposed-method">
                                    <MethodIcon status={item.lifecycleStatus}/>
                                    {retirementLabel(item.lifecycleStatus) ?? 'Выбыло'} · {formatDate(item.disposalDate)}
                                </p>
                                {/* Два ответа на «что дальше» в одном ряду: посмотреть, что с вещью
                                    было (D13), и исправить неверно записанное выбытие (D05). */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={`/clothing/${item.id}`} data-testid="disposed-history-link"
                                          className="inline-flex items-center min-h-[44px] text-xs
                                                     text-brand-600 dark:text-brand-400 hover:underline">
                                        История вещи
                                    </Link>
                                    {canCorrect && item.lifecycleStatus !== 'SOLD' && (
                                        <CorrectionPanel itemId={item.id} name={item.name}
                                                         onDone={() => void load()}/>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {!loading && !error && total > 0 && (
                <div className="flex justify-end pt-2">
                    <PageSizeSelect
                        table="disposal-archive"
                        label="Записей на странице"
                        value={pageSize}
                        onChange={setPageSize}
                        totalElements={total}
                    />
                </div>
            )}

            {!loading && !error && totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-2">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            className="btn-secondary min-h-[44px] px-4 rounded-xl disabled:opacity-50">
                        Назад
                    </button>
                    <span className="flex items-center px-4 text-gray-600 dark:text-gray-400">
                        {page + 1} / {totalPages}
                    </span>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                            className="btn-secondary min-h-[44px] px-4 rounded-xl disabled:opacity-50">
                        Вперёд
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Срез архива ────────────────────────────────────────────────────────────────

/**
 * Панель среза. Способы — чипы с числами из итогов: так сразу видно, чего в архиве много, и
 * фильтр заодно работает подсказкой. Категория и даты — обычные поля: их значений слишком
 * много для чипов.
 */
const ArchiveFilters: React.FC<{
    search: string;
    filter: DisposalArchiveFilter;
    summary: DisposalArchiveSummary | null;
    filtered: boolean;
    onSearch: (value: string) => void;
    onToggleMethod: (code: string) => void;
    onChange: (patch: Partial<DisposalArchiveFilter>) => void;
    onReset: () => void;
}> = ({search, filter, summary, filtered, onSearch, onToggleMethod, onChange, onReset}) => {
    // Без итогов (сбой запроса) показываем полный список способов: фильтр должен работать всегда.
    const methods = summary?.methods?.length
        ? summary.methods
        : RETIRED_STATUSES.map(code => ({code, count: 0}));
    const selected = filter.method ?? [];

    return (
        <div className="card p-4 space-y-3" data-testid="disposed-filters">
            <label className="block">
                <span className="sr-only">Поиск по названию и бренду</span>
                <input
                    type="search"
                    value={search}
                    onChange={e => onSearch(e.target.value)}
                    placeholder="Поиск по названию или бренду"
                    data-testid="disposed-search"
                    className="input-field w-full min-h-[44px]"
                />
            </label>

            <div className="flex flex-wrap gap-2" role="group" aria-label="Фильтр по способу выбытия">
                {methods.map(m => {
                    const active = selected.includes(m.code);
                    return (
                        <button key={m.code} type="button" aria-pressed={active}
                                onClick={() => onToggleMethod(m.code)}
                                data-testid={`disposed-method-${m.code}`}
                                className={`min-h-[44px] px-3 rounded-xl text-sm border transition-colors
                                            duration-ui ease-out ${
                                    active
                                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-300'
                                        : 'border-gray-200 dark:border-ink-700 text-gray-700 dark:text-gray-200 hover:border-brand-500'
                                }`}>
                            {methodLabel(m.code)}
                            {m.count > 0 && (
                                <span className="ml-1 text-xs tabular-nums opacity-70">{m.count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="block mb-1">Категория</span>
                    <select
                        value={filter.categoryId ?? ''}
                        onChange={e => onChange({categoryId: e.target.value || null})}
                        data-testid="disposed-category"
                        className="input-field w-full min-h-[44px]"
                    >
                        <option value="">Любая</option>
                        {(summary?.categories ?? []).map(c => (
                            <option key={c.id} value={c.id}>{c.name ?? 'Без названия'}</option>
                        ))}
                    </select>
                </label>
                <label className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="block mb-1">Выбыли с</span>
                    <input type="date" value={filter.from ?? ''} data-testid="disposed-from"
                           onChange={e => onChange({from: e.target.value || null})}
                           className="input-field w-full min-h-[44px]"/>
                </label>
                <label className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="block mb-1">по</span>
                    <input type="date" value={filter.to ?? ''} data-testid="disposed-to"
                           onChange={e => onChange({to: e.target.value || null})}
                           className="input-field w-full min-h-[44px]"/>
                </label>
            </div>

            {filtered && (
                <button type="button" onClick={onReset} data-testid="disposed-reset"
                        className="min-h-[44px] px-4 rounded-xl text-sm text-brand-600 dark:text-brand-400 hover:underline">
                    Сбросить фильтры
                </button>
            )}
        </div>
    );
};

/**
 * Итоги среза: сколько вещей и как они уходили. Каналы отвечают на вопрос «переработка или
 * свалка», источники — «сам довожу до конца или за меня это делает автоматика». Второй
 * разрез спрятан под раскрытие: он интересен реже, но без него архив не объясняет привычку.
 */
const ArchiveTotals: React.FC<{summary: DisposalArchiveSummary}> = ({summary}) => {
    // Итоги — подсказка: неполный ответ не должен ронять список, ради которого пришли.
    const byMethod = summary.byMethod ?? [];
    const bySource = summary.bySource ?? [];
    if (!summary.total || byMethod.length === 0) {
        return null;
    }
    return (
        <div className="card p-4" data-testid="disposed-totals">
            <p className="text-sm text-gray-900 dark:text-gray-100">
                В этом срезе: {pluralItems(summary.total)}
            </p>
            <ul className="mt-2 flex flex-wrap gap-2" data-testid="disposed-by-method">
                {byMethod.map(b => (
                    <li key={b.code} className="chip text-xs">
                        {methodLabel(b.code)}: <span className="tabular-nums">{b.count}</span>
                    </li>
                ))}
            </ul>
            {bySource.length > 0 && (
                <details className="mt-3">
                    <summary className="cursor-pointer min-h-[44px] flex items-center text-sm
                                        text-gray-700 dark:text-gray-200">
                        Как принимались решения
                    </summary>
                    <ul className="mt-2 flex flex-wrap gap-2" data-testid="disposed-by-source">
                        {bySource.map(b => (
                            <li key={b.code} className="chip text-xs">
                                {disposalSourceLabel(b.code)}: <span className="tabular-nums">{b.count}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
};

export default DisposedItemsPage;
